import React from 'react'
import { Link } from 'react-router-dom'
import Table from '../../../components/common/Table/Table'
import Loader from '../../../components/common/Loader'
import ValidationBanner from '../../../components/common/ValidationBanner'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import StatusIconWithTooltip from '../../../components/common/StatusIconWithTooltip'

interface CurrentRequestsListProps {
}

/**
 * CurrentRequestsList - Displays paginated current time-off requests using list view API
 * Uses local state for independent pagination with direct API calls
 */
const CurrentRequestsList: React.FC<CurrentRequestsListProps> = () => {
  // Local state for data and pagination
  const [currentPage, setCurrentPage] = React.useState(1)
  const [sortOrder, setSortOrder] = React.useState<'ASC' | 'DESC'>('ASC')
  const [sortColumn, setSortColumn] = React.useState<'fromDate' | 'hoursRequested' | undefined>(undefined)
  const [activeSortColumn, setActiveSortColumn] = React.useState<string>('dateRequestedSortValue')
  const [requests, setRequests] = React.useState<any[]>([])
  const [totalRecords, setTotalRecords] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const pageSize = 20
 const isAdminMMode = globalThis.location.pathname.startsWith('/admin')
  React.useEffect(() => {
    const fetchCurrentRequests = async () => {
      try {
        setLoading(true)
        setError(null)
        const now = new Date()
        const year = now.getFullYear()
        const month = now.getMonth() + 1
        const day = now.getDate()
        
        
        // Call the list view API endpoint with proper structure
        const url = endpoints.requester.getAllTimeOffReqList({
          year,
          month,
          day,
          page: currentPage,
          perPage: pageSize,
          sortOrder,
          sortColumn,
        })
      
        const response = await api.get(url)
        
        // Parse response with proper structure
        const responseData = response.data
        
        // Extract items array
        const items = responseData.items || []
        
        // Extract pagination info from meta
        const total = responseData.meta?.total || 0
   
        
        setRequests(items)
        setTotalRecords(total)
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || err.response?.data?.details?.[0]?.message || err.message || 'Failed to load requests'
        setError(errorMessage)
        setRequests([])
        setTotalRecords(0)
      } finally {
        setLoading(false)
      }
    }
    
    fetchCurrentRequests()
  }, [currentPage, pageSize, sortOrder, sortColumn])

  const handleNavigation = (requestId: string) => {
    return isAdminMMode ? `/admin/requestor/status/${requestId}` : `/requestor/status/${requestId}`
  }
  const getTableRows = () => {
    return requests
      .map((r: any) => ({
        id: r.requestId,
        dateRequested: (
          <div className="flex items-center justify-between gap-3">
            <Link to={handleNavigation(r.requestId)} className="p3 -medium underline!" style={{ color: '#0074AD', textDecoration: 'underline', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact', colorScheme: 'light' }}>
              {r.todate}
            </Link>
            <StatusIconWithTooltip
              statusCode={r.statusCode}
              alt={r.status}
            />
          </div>
        ),
        hoursRequested: r.hoursRequested,
        timeOffType: r.timeOffReqstType,
        status: r.status,
        detail: r.detail || '-',
      }))
  }

  const columns = [
    { key: 'dateRequested', label: 'Date Requested', isSortable: true, sortKey: 'dateRequestedSortValue' },
    { key: 'hoursRequested', label: 'Hours Requested', isSortable: true },
    { key: 'timeOffType', label: 'Time off Request Type' },
    { key: 'status', label: 'Status' },
    { key: 'detail', label: 'Detail' },
  ]

  const rows = getTableRows()

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleSort = (columnKey: string, direction: 'ASC' | 'DESC') => {
    setSortOrder(direction)
    const apiSortColumn = columnKey === 'hoursRequested' ? 'hoursRequested' : 'fromDate'
    setSortColumn(apiSortColumn)
    setActiveSortColumn(columnKey)
    setCurrentPage(1)
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96 py-12">
        <ValidationBanner variant="error" heading={error} />
      </div>
    )
  }

  if (requests.length === 0 && !loading) {
    return (
      <div className="flex items-center justify-center min-h-96 py-12">
        <div className="text-center">
          <p className="p3 -medium">No time off requests available</p>
        </div>
      </div>
    )
  }

  return (
    <>
      {loading && <Loader />}
      <div className="relative">
        <Table
          columns={columns}
          rows={rows}
          showPagination={true}
          paginationProps={{ totalItems: totalRecords, itemsPerPage: pageSize }}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onSort={handleSort}
          sortColumn={activeSortColumn}
          sortDirection={sortOrder}
        />
      </div>
    </>
  )
}

export default CurrentRequestsList
